<template>
  <div class="fm-page-shell">
    <!-- ===== CONTEXTUAL RAIL ===== -->
    <aside class="fm-rail">
      <div class="fm-rail-label">Events</div>

      <button type="button" class="fm-view" :class="{ 'fm-view--active': resultView === 'all' }" @click="resultView = 'all'">
        <span class="fm-view-dot" style="background:#94A3B8"></span>All
        <span class="fm-view-count">{{ auditLogs.length }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': resultView === 'success' }" @click="resultView = 'success'">
        <span class="fm-view-dot" style="background:#10B981"></span>Success
        <span class="fm-view-count">{{ successCount }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': resultView === 'failed' }" @click="resultView = 'failed'">
        <span class="fm-view-dot" style="background:#EF4444"></span>Failed
        <span class="fm-view-count">{{ failedCount }}</span>
      </button>

      <div v-if="categoryList.length" class="fm-rail-label fm-rail-label--type">Type</div>
      <button v-if="categoryList.length" type="button" class="fm-view" :class="{ 'fm-view--active': typeView === 'all' }" @click="typeView = 'all'">
        <i class="fa-solid fa-layer-group fm-view-ico"></i>All types
        <span class="fm-view-count">{{ auditLogs.length }}</span>
      </button>
      <button v-for="c in categoryList" :key="c" type="button" class="fm-view" :class="{ 'fm-view--active': typeView === c }" @click="typeView = c">
        <i class="fa-solid fm-view-ico" :class="categoryIcon(c)"></i>{{ c }}
        <span class="fm-view-count">{{ categoryCounts[c] }}</span>
      </button>

    </aside>

    <!-- ===== MAIN ===== -->
    <main class="fm-main">
      <b-overlay :show="loading" spinner small>
        <div v-if="notAuthorized" class="fm-page">
          <div class="fm-empty">You are not authorized to view this page</div>
        </div>
        <div v-else class="fm-page">
          <div class="fm-page-head">
            <div class="fm-page-title">Audit Log</div>
            <button class="fm-btn fm-btn--ghost" @click="showFilters = !showFilters">
              <i class="fa-solid fa-sliders"></i>{{ showFilters ? 'Hide Filters' : 'Show Filters' }}
            </button>
          </div>
          <div class="fm-page-desc">A record of every administrative and system action.</div>

          <div class="fm-toolbar">
            <div class="fm-search">
              <i class="fa-solid fa-magnifying-glass"></i>
              <b-form-input type="search" v-model="filters.search" @input="debouncedFetch"
                            placeholder="Search actions, methods, endpoints..."></b-form-input>
            </div>
            <div v-if="totalLogs > perPage" class="fm-toolbar-pager">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalLogs"
                :per-page="perPage"
                size="sm"
                aria-controls="audit-table"
              />
            </div>
          </div>

          <!-- Filters Section (preserved, restyled) -->
          <b-collapse v-model="showFilters" class="mb-3">
            <div class="filters-section">
              <b-row class="filter-row">
                <b-col lg="4" md="6" class="mb-3">
                  <div class="filter-group">
                    <label class="filter-label">User</label>
                    <b-form-input v-model="filters.user" placeholder="Enter username..." class="filter-input" @input="debouncedFetch" />
                  </div>
                </b-col>
                <b-col lg="4" md="6" class="mb-3">
                  <div class="filter-group">
                    <label class="filter-label">Start Date</label>
                    <b-form-input v-model="filters.startDate" type="datetime-local" class="filter-input" @input="fetchAuditLogs" />
                  </div>
                </b-col>
                <b-col lg="4" md="6" class="mb-3">
                  <div class="filter-group">
                    <label class="filter-label">End Date</label>
                    <b-form-input v-model="filters.endDate" type="datetime-local" class="filter-input" @input="fetchAuditLogs" />
                  </div>
                </b-col>
              </b-row>
              <div class="filters-actions">
                <b-button variant="outline-danger" size="sm" @click="clearFilters" class="clear-btn">
                  <i class="nc-icon nc-simple-remove mr-1"></i>Clear All Filters
                </b-button>
                <div class="active-filters" v-if="hasActiveFilters">
                  <small class="text-muted">{{ activeFilterCount }} filter(s) active</small>
                </div>
              </div>
            </div>
          </b-collapse>

          <!-- List -->
          <div v-if="filteredLogs.length" class="fm-list">
            <div v-for="log in filteredLogs" :key="log.EventId" class="fm-row" @click="openRow(log)">
              <div class="fm-row-main">
                <div class="fm-row-top">
                  <i class="fa-solid fm-row-icon" :class="actionIcon(log)"></i>
                  <span class="fm-row-name">{{ log.Action }}</span>
                  <i class="fa-regular fa-copy fm-row-copy"></i>
                  <span v-if="log.User" class="fm-tag">{{ log.User }}</span>
                  <span v-if="log.Method" class="fm-tag">{{ log.Method }}</span>
                </div>
                <div v-if="describeAudit(log)" class="fm-row-desc">{{ describeAudit(log) }}</div>
              </div>
              <span v-if="log.Timestamp" class="fm-value-pill fm-mono">{{ formatDateTime(log.Timestamp) }}</span>
              <div class="fm-status-cell">
                <span class="fm-status-pill" :class="'fm-status-pill--' + statusTone(log.Status)">
                  <span v-if="statusTone(log.Status) !== 'neu'" class="fm-dot"></span>{{ statusLabel(log.Status) }}
                </span>
              </div>
              <i class="fa-solid fa-angle-right fm-chev"></i>
            </div>
          </div>
          <div v-else-if="auditLogs.length" class="fm-empty">No events match this filter.</div>
          <div v-else class="fm-empty">No audit events found.</div>

          <!-- Hidden table drives sorting/pagination controls (aria-controls target preserved) -->
          <b-table
            id="audit-table"
            class="d-none"
            :items="auditLogs"
            :fields="auditFields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :busy="loading"
            small
          ></b-table>

          <!-- Request Details Modal -->
          <b-modal
            id="request-details-modal"
            v-model="showRequestModal"
            title="Request Details"
            size="lg"
            scrollable
            hide-footer
            header-bg-variant="light"
          >
            <div v-if="selectedRequest">
              <div class="json-viewer">
                <div v-for="(value, key) in parsedRequestData" :key="key" class="json-property">
                  <div class="json-property-row">
                    <span class="json-property-key">{{ formatKey(key) }}:</span>
                    <span v-if="isObject(value)" class="json-property-value">
                      <b-badge variant="light" @click="toggleObject(key)" class="object-badge">
                        {{ expandedObjects.includes(key) ? 'Collapse' : 'Expand' }} Object
                      </b-badge>
                    </span>
                    <span v-else-if="isArray(value)" class="json-property-value">
                      <b-badge variant="light" @click="toggleObject(key)" class="object-badge">
                        {{ expandedObjects.includes(key) ? 'Collapse' : 'Expand' }} Array ({{ value.length }} items)
                      </b-badge>
                    </span>
                    <span v-else class="json-property-value" :class="valueTypeClass(value)">
                      {{ formatValue(value) }}
                    </span>
                  </div>

                  <div v-if="expandedObjects.includes(key) && isObject(value)" class="json-nested-object">
                    <div v-for="(nestedValue, nestedKey) in value" :key="`${key}-${nestedKey}`" class="json-property-row nested">
                      <span class="json-property-key">{{ formatKey(nestedKey) }}:</span>
                      <span class="json-property-value" :class="valueTypeClass(nestedValue)">
                        {{ formatValue(nestedValue) }}
                      </span>
                    </div>
                  </div>

                  <div v-if="expandedObjects.includes(key) && isArray(value)" class="json-nested-object">
                    <div v-for="(item, index) in value" :key="`${key}-${index}`" class="json-property-row nested">
                      <span class="json-property-key">[{{ index }}]:</span>
                      <span class="json-property-value" :class="valueTypeClass(item)">
                        {{ formatValue(item) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-modal>

          <!-- Error Details Modal -->
          <b-modal
            id="error-details-modal"
            v-model="showErrorModal"
            title="Error Details"
            size="lg"
            header-bg-variant="danger"
            header-text-variant="white"
            hide-footer
          >
            <div class="error-details">
              <pre class="error-message">{{ selectedError }}</pre>
            </div>
          </b-modal>
        </div>
      </b-overlay>
    </main>

    <!-- ===== DETAIL DRAWER ===== -->
    <detail-drawer
      :open="drawerOpen"
      kind="Audit Event"
      :title="selected.Action || ''"
      :status="selected.Status || ''"
      :tone="statusTone(selected.Status)"
      :fields="drawerFields"
      @close="closeDrawer">
      <template #footer>
        <button v-if="selected.Request" class="fm-btn fm-btn--ghost" @click="drawerAction('request')">
          <i class="fa-regular fa-file-code"></i>View Request
        </button>
        <button v-if="selected.Error" class="fm-btn fm-btn--danger" @click="drawerAction('error')">
          <i class="fa-solid fa-triangle-exclamation"></i>View Error
        </button>
        <button class="fm-btn fm-btn--ghost" @click="copyJson">
          <i class="fa-regular fa-copy"></i>Copy JSON
        </button>
      </template>
    </detail-drawer>
  </div>
</template>

<script>
import { BOverlay, BTable, BPagination, BButton, BModal, BBadge, BRow, BCol, BFormInput, BCollapse } from "bootstrap-vue";
import DetailDrawer from "@/components/DetailDrawer.vue";
import apiClient from "@/http"; // Assuming you have a configured API client

export default {
  components: {
    DetailDrawer,
    BOverlay,
    BTable,
    BPagination,
    BButton,
    BModal,
    BBadge,
    BRow,
    BCol,
    BFormInput,
    BCollapse,
  },
  data() {
    return {
      auditLogs: [],
      agentNames: {}, // map: AgentToken -> friendly Name
      auditFields: [
        { key: "Timestamp", label: "Date & Time", sortable: true },
        { key: "EventId", label: "Event ID", sortable: true },
        { key: "Action", label: "Action", sortable: true },
        { key: "Method", label: "Method", sortable: true },
        { key: "Endpoint", label: "Endpoint", sortable: true },
        { key: "Status", label: "Status", sortable: true },
        { key: "Target", label: "Target", sortable: true },
        { key: "User", label: "User", sortable: true },
        { key: "Request", label: "Request", sortable: false },
        { key: "Error", label: "Error", sortable: false },
      ],
      loading: false,
      currentPage: 1,
      perPage: 20,
      totalLogs: 0,
      sortBy: "Timestamp",
      sortDesc: true,
      notAuthorized: false,
      // Filter data properties
      filters: {
        user: "",
        startDate: "",
        endDate: "",
        search: "",
      },
      showFilters: false, // Collapsed by default
      resultView: "all", // page-local result filter: all | success | failed
      typeView: "all", // page-local category filter: all | <category name>
      // New data properties for JSON display
      showRequestModal: false,
      showErrorModal: false,
      selectedRequest: null,
      selectedError: null,
      expandedObjects: [], // Track which objects are expanded
      // Debounce timer
      debounceTimer: null,
      // Drawer state
      drawerOpen: false,
      selected: {},
    };
  },
  methods: {
    openRow(log) {
      this.selected = log;
      this.drawerOpen = true;
    },
    closeDrawer() {
      this.drawerOpen = false;
    },
    drawerAction(type) {
      const item = this.selected;
      this.closeDrawer();
      if (type === "request") {
        this.showRequestDetails(item);
      } else if (type === "error") {
        this.showErrorDetails(item);
      }
    },
    copyJson() {
      try {
        const json = JSON.stringify(this.selected, null, 2);
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(json);
        }
      } catch (e) {
        console.error("Error copying JSON:", e);
      }
    },
    // Map a Status value to a drawer/pill tone. Status is an HTTP code (201, 500),
    // but keep the string fallback so the drawer works for non-numeric values.
    statusTone(status) {
      const n = Number(status);
      if (!Number.isNaN(n) && n > 0) {
        if (n >= 200 && n < 300) return "suc";
        if (n >= 300 && n < 400) return "warn";
        if (n >= 400) return "err";
      }
      const s = String(status || "").toLowerCase();
      if (s.includes("success") || s === "ok" || s === "info" || s === "granted") return "suc";
      if (s.includes("fail") || s.includes("error") || s.includes("denied")) return "err";
      if (s.includes("warn") || s.includes("pending")) return "warn";
      return "neu";
    },
    // Short semantic label for an HTTP status code, e.g. "201 Created", "500 Failed".
    statusLabel(status) {
      const n = Number(status);
      if (Number.isNaN(n) || n <= 0) return String(status || "");
      const phrases = {
        200: "OK", 201: "Created", 202: "Accepted", 204: "No Content",
        400: "Bad Request", 401: "Unauthorized", 403: "Forbidden", 404: "Not Found",
        409: "Conflict", 422: "Unprocessable", 500: "Failed", 502: "Unavailable", 503: "Unavailable",
      };
      return phrases[n] ? `${n} ${phrases[n]}` : String(n);
    },
    // Safe JSON parse for Request/Target strings; returns {} on failure.
    safeParse(json) {
      if (!json) return {};
      if (typeof json === "object") return json;
      try {
        return JSON.parse(json);
      } catch (e) {
        return {};
      }
    },
    // If a request's agent_name holds a known agent token, swap in the friendly name.
    resolveAgentTokens(data) {
      if (!data || typeof data !== "object" || Array.isArray(data)) return data;
      const token = data.agent_name;
      if (typeof token === "string" && this.agentNames[token]) {
        return { ...data, agent_name: this.agentNames[token] };
      }
      return data;
    },
    // Group an audit entry into a coarse category for filtering/icons.
    actionCategory(log) {
      const a = String((log && log.Action) || "").toLowerCase();
      if (a.includes("role") || a.includes("resource")) return "Roles";
      if (a.includes("user")) return "Users";
      if (a.includes("agent")) return "Agents";
      if (a.includes("access") || a.includes("grant") || a.includes("polic") || a.includes("request")) return "Access";
      if (a.includes("connect") || a.includes("network") || a.includes("joined")) return "Network";
      if (a.includes("sso")) return "SSO";
      if (a.includes("service")) return "Services";
      return "Other";
    },
    // FontAwesome icon class for a category.
    categoryIcon(category) {
      const icons = {
        Roles: "fa-key", Users: "fa-user", Agents: "fa-robot", Access: "fa-id-badge",
        Network: "fa-network-wired", SSO: "fa-right-to-bracket", Services: "fa-server", Other: "fa-circle-info",
      };
      return icons[category] || "fa-circle-info";
    },
    // FontAwesome icon class for an action, via its category.
    actionIcon(log) {
      return this.categoryIcon(this.actionCategory(log));
    },
    // Turn an audit entry into a plain-English description from its payload.
    describeAudit(log) {
      const req = this.safeParse(log.Request);
      const tgt = this.safeParse(log.Target);
      const q = (v) => (v ? ` "${v}"` : "");
      const subtypeLabels = { mysql: "MySQL", postgres: "Postgres", ssh: "SSH", k8s: "Kubernetes", tcp: "TCP" };
      const sub = subtypeLabels[tgt.rSubType] || tgt.rSubType || "";
      const arrLen = Array.isArray(req.ServiceAccessIds) ? req.ServiceAccessIds.length : 0;
      switch (log.Action) {
        case "Create Role": {
          const parts = ["Created", sub, tgt.rType, "role"].filter(Boolean).join(" ");
          return parts + q(req.Name);
        }
        case "Delete a role": return "Deleted a role";
        case "Invite user": return `Invited ${req.Email || req.email || "a user"}${(req.Role || req.role) ? ` as ${req.Role || req.role}` : ""}`;
        case "Remove user": return "Removed a user";
        case "Change user role": return "Changed a user's role";
        case "Change user status": return "Changed a user's status";
        case "Create a new Service": return `Created service${q(req.ServiceName)}${req.ServiceSubType ? ` (${req.ServiceSubType})` : ""}`;
        case "Call service action": return `${(req.action === "deactivate" ? "Deactivated" : "Activated")} service${q(tgt.Name)}`;
        case "Delete a service": return `Deleted service${q(tgt.Name)}`;
        case "Add an agent": return "Added an agent";
        case "Delete an agent": return `Deleted agent${q(tgt.name)}`;
        case "Create a service access request": return `Requested access to${q(req.ServiceName)}`;
        case "Grant service access": return `Granted access to${q(req.ServiceName)}`;
        case "Service request action": return `${(req.action === "reject" ? "Rejected" : "Approved")} an access request`;
        case "Revoke Service Access": return "Revoked service access";
        case "Extend Service Access": return "Extended service access";
        case "Bulk Revoke Service Access": return `Revoked access for ${arrLen} grant${arrLen === 1 ? "" : "s"}`;
        case "Bulk Extend Service Access": return `Extended access for ${arrLen} grant${arrLen === 1 ? "" : "s"}`;
        case "Update Access Policies": return "Updated access policies";
        case "Agent Joined": return `Agent${q(this.agentNames[req.agent_name] || req.agent_hostname || req.agent_ip)} joined the network`;
        case "Service connected": return `Service${q(req.ServiceName)} connected`;
        case "Service client connected": return `Client connected to service${q(req.ServiceName)}`;
        case "Update SSO settings": return "Updated SSO settings";
        default: return log.Target || log.Endpoint || "";
      }
    },
    async fetchAuditLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        };

        // Add filters if they have values
        if (this.filters.user) params.user = this.filters.user;
        if (this.filters.startDate) params.startDate = this.filters.startDate;
        if (this.filters.endDate) params.endDate = this.filters.endDate;
        if (this.filters.search) params.search = this.filters.search;

        const response = await apiClient.get("/audit", { params });
        this.auditLogs = response.data.Logs;
        this.totalLogs = response.data.Total;
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.notAuthorized = true;
        }
        console.error("Failed to fetch audit logs:", error);
      } finally {
        this.loading = false;
      }
    },
    // Build a token -> friendly name map so audit events can show agent names.
    async fetchAgents() {
      try {
        const response = await apiClient.get("/agents");
        const map = {};
        for (const a of response.data || []) {
          if (a.AgentToken) map[a.AgentToken] = a.Name;
        }
        this.agentNames = map;
      } catch (e) {
        // Non-fatal: descriptions fall back to hostname/IP.
      }
    },
    // Clear all filters
    clearFilters() {
      this.filters = {
        user: "",
        startDate: "",
        endDate: "",
        search: "",
      };
      this.currentPage = 1;
      this.fetchAuditLogs();
    },
    // Debounced fetch for search/filter inputs
    debouncedFetch() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1; // Reset to first page when filtering
        this.fetchAuditLogs();
      }, 500);
    },
    formatDateTime(dateTime) {
      return new Date(dateTime).toLocaleString();
    },
    // Methods for handling request details
    showRequestDetails(item) {
      try {
        this.selectedRequest = item.Request;
        this.expandedObjects = []; // Reset expanded objects
        this.showRequestModal = true;
      } catch (error) {
        console.error("Error displaying request details:", error);
      }
    },
    // Methods for handling error details
    showErrorDetails(item) {
      this.selectedError = item.Error;
      this.showErrorModal = true;
    },
    // Format a key to be more readable (convert camelCase to Title Case with spaces)
    formatKey(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    },
    // Format values based on their type
    formatValue(value) {
      if (value === null) return 'null';
      if (value === undefined) return 'undefined';
      if (typeof value === 'boolean') return value.toString();
      if (typeof value === 'number') return value.toString();
      if (typeof value === 'string') {
        // Check if it's a timestamp/date string
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
          try {
            return new Date(value).toLocaleString();
          } catch (e) {
            return value;
          }
        }
        return value;
      }
      if (this.isObject(value) || this.isArray(value)) {
        return JSON.stringify(value);
      }
      return String(value);
    },
    // Check if value is an object (but not array or null)
    isObject(value) {
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    },
    // Check if value is an array
    isArray(value) {
      return Array.isArray(value);
    },
    // Return CSS class based on value type
    valueTypeClass(value) {
      if (value === null || value === undefined) return 'type-null';
      if (typeof value === 'boolean') return 'type-boolean';
      if (typeof value === 'number') return 'type-number';
      if (typeof value === 'string') {
        // Check if it's a timestamp/date string
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
          return 'type-date';
        }
        return 'type-string';
      }
      return '';
    },
    // Toggle expanded state of an object
    toggleObject(key) {
      if (this.expandedObjects.includes(key)) {
        this.expandedObjects = this.expandedObjects.filter(k => k !== key);
      } else {
        this.expandedObjects.push(key);
      }
    }
  },
  computed: {
    // Page-local result + category filtering for the rail views (no new API calls)
    filteredLogs() {
      let logs = this.auditLogs;
      if (this.resultView === "success") {
        logs = logs.filter(l => this.statusTone(l.Status) === "suc");
      } else if (this.resultView === "failed") {
        logs = logs.filter(l => this.statusTone(l.Status) === "err");
      }
      if (this.typeView !== "all") {
        logs = logs.filter(l => this.actionCategory(l) === this.typeView);
      }
      return logs;
    },
    successCount() {
      return this.auditLogs.filter(l => this.statusTone(l.Status) === "suc").length;
    },
    failedCount() {
      return this.auditLogs.filter(l => this.statusTone(l.Status) === "err").length;
    },
    // Count of events per category on the current page.
    categoryCounts() {
      const counts = {};
      for (const l of this.auditLogs) {
        const c = this.actionCategory(l);
        counts[c] = (counts[c] || 0) + 1;
      }
      return counts;
    },
    // Categories present on the current page, in a stable display order.
    categoryList() {
      const order = ["Access", "Agents", "Network", "Roles", "Services", "Users", "SSO", "Other"];
      return order.filter(c => this.categoryCounts[c]);
    },
    // Drawer fields for the selected audit event
    drawerFields() {
      const s = this.selected;
      if (!s || !s.Action) return [];
      return [
        { label: "Time", value: s.Timestamp ? this.formatDateTime(s.Timestamp) : "N/A", mono: true },
        { label: "Actor", value: s.User || "N/A" },
        { label: "Target", value: s.Target || s.Endpoint || "N/A" },
        { label: "Result", value: s.Status ? this.statusLabel(s.Status) : "N/A" },
        { label: "Method", value: s.Method || "N/A" },
        { label: "Event ID", value: s.EventId ? String(s.EventId) : "N/A", mono: true },
      ];
    },
    // Parse the request data and handle different formats
    parsedRequestData() {
      try {
        if (!this.selectedRequest) return {};

        let data;
        if (typeof this.selectedRequest === 'object' && this.selectedRequest !== null) {
          data = this.selectedRequest;
        } else if (typeof this.selectedRequest === 'string') {
          try {
            data = JSON.parse(this.selectedRequest);
          } catch (e) {
            return { value: this.selectedRequest };
          }
        } else {
          return { value: String(this.selectedRequest) };
        }

        return this.resolveAgentTokens(data);
      } catch (error) {
        console.error("Error parsing request data:", error);
        return { error: "Unable to parse request data" };
      }
    },
    // Check if any filters are active
    hasActiveFilters() {
      return this.filters.user ||
             this.filters.startDate ||
             this.filters.endDate ||
             this.filters.search;
    },
    // Count active filters
    activeFilterCount() {
      let count = 0;
      if (this.filters.user) count++;
      if (this.filters.startDate) count++;
      if (this.filters.endDate) count++;
      if (this.filters.search) count++;
      return count;
    }
  },
  watch: {
    currentPage: "fetchAuditLogs",
    sortBy: "fetchAuditLogs",
    sortDesc: "fetchAuditLogs",
  },
  mounted() {
    this.fetchAuditLogs();
    this.fetchAgents();
  },
};
</script>

<style scoped>
/* Request Details — ledger-style definition grid */
.json-viewer {
  font-family: var(--fm-font-sans);
  padding: 2px 2px 4px;
  max-height: 520px;
  overflow-y: auto;
}

.json-property {
  border-bottom: 1px solid var(--fm-border, #EEF2F6);
}

.json-property:last-child {
  border-bottom: none;
}

.json-property-row {
  display: grid;
  grid-template-columns: minmax(150px, 33%) 1fr;
  gap: 18px;
  align-items: baseline;
  padding: 12px 6px;
}

.json-property-key {
  font-family: var(--fm-font-sans);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: var(--fm-slate-500, #64748B);
  white-space: nowrap;
}

.json-property-value {
  font-family: var(--fm-font-mono);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--fm-slate-800, #1E293B);
  word-break: break-word;
  line-height: 1.5;
}

.json-nested-object {
  grid-column: 1 / -1;
  margin: 4px 0 6px;
  padding: 6px 0 6px 16px;
  border-left: 2px solid var(--fm-border, #E2E8F0);
}

.json-nested-object .json-property-row {
  padding: 7px 4px;
  gap: 14px;
}

.type-string { color: #2563EB; }
.type-number { color: #059669; }
.type-boolean { color: #D97706; }
.type-null {
  color: var(--fm-slate-400, #94A3B8);
  font-style: italic;
}
.type-date { color: #7C3AED; }

.object-badge {
  cursor: pointer;
  background-color: var(--fm-surface-muted, #F1F5F9);
  color: var(--fm-slate-600, #475569);
  font-family: var(--fm-font-sans);
  font-weight: 500;
  letter-spacing: 0.01em;
  border: 1px solid var(--fm-border, #E2E8F0);
  transition: all 0.15s var(--fm-ease, ease);
}

.object-badge:hover {
  background-color: #E2E8F0;
  color: var(--fm-slate-800, #1E293B);
}

.error-details {
  background-color: #FEF2F2;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #FECACA;
}

.error-message {
  color: #B91C1C;
  margin: 0;
  white-space: pre-wrap;
  font-family: 'Roboto Mono', monospace;
  font-size: 0.9em;
}

/* Filter Section (Flagsmith-style tweak) */
.filters-section {
  background: var(--fm-surface-muted);
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-r-card);
  padding: 18px;
}

.filter-label {
  display: block;
  font-weight: 600;
  color: var(--fm-slate-600);
  margin-bottom: 6px;
  font-size: 0.85rem;
}

.filters-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.active-filters {
  color: var(--fm-slate-500);
}

.fm-row-icon {
  color: var(--fm-slate-500, #64748B);
  font-size: 0.85rem;
  width: 16px;
  text-align: center;
  flex: 0 0 auto;
}

.fm-rail-label--type {
  margin-top: 22px;
}

.fm-view-ico {
  width: 14px;
  margin-right: 8px;
  font-size: 0.8rem;
  color: var(--fm-slate-500, #64748B);
  text-align: center;
}

.fm-view--active .fm-view-ico {
  color: var(--fm-accent, #2563EB);
}

/* ── Typographic polish (scoped to the Audit page) ── */
.fm-rail-label {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--fm-slate-400, #94A3B8);
}

.fm-view-count {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

.fm-page-title {
  letter-spacing: -0.02em;
  font-weight: 700;
}

.fm-page-desc {
  color: var(--fm-slate-500, #64748B);
}

.fm-row-name {
  font-weight: 600;
  letter-spacing: -0.005em;
  color: var(--fm-slate-900, #0F172A);
}

.fm-row-desc {
  color: var(--fm-slate-500, #64748B);
  font-size: 12.5px;
}

.fm-value-pill.fm-mono {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  letter-spacing: -0.01em;
  color: var(--fm-slate-500, #64748B);
}

.fm-status-pill {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.005em;
}
</style>
